import {
  BladMagazynu,
  listaSetlist,
  listaUtworow,
  MAGAZYN_SETLISTY,
  MAGAZYN_UTWORY,
  otworzBaze,
  type Setlista,
  type Utwor,
} from './db.ts';

// Eksport i import całej bazy do jednego pliku JSON. Realne zabezpieczenie
// śpiewnika (patrz nagłówek `db.ts` po powody), więc plik ma być czytelny,
// wersjonowany i odporny na uszkodzone dane wejściowe: import waliduje
// WSZYSTKO przed jakimkolwiek zapisem, a sam zapis jest jedną transakcją.

const WERSJA_FORMATU_PACZKI = 1;

export interface PaczkaEksportu {
  format: 'ognisko-spiewnik';
  wersjaFormatu: 1;
  zapisanoO: string; // ISO 8601
  utwory: Utwor[];
  /** Setlisty (etap 8). OPCJONALNE, ten sam powód co przy `transpozycja`/`capo`
   * w `Utwor`: plik zapisany przed tym etapem po prostu tego pola nie ma, a
   * podniesienie `wersjaFormatu` sprawiłoby, że taki plik zostałby odrzucony
   * jako "nieznana wersja formatu" - aktualizacja aplikacji unieważniłaby
   * wtedy kopię zapasową MH sprzed etapu 8. */
  setlisty?: Setlista[];
}

/** Sprawdza pojedynczy `Utwor` z paczki, dopisując powody odrzucenia po
 * polsku do `powody`. Nie rzuca - kompletna lista powodów jest cenniejsza
 * niż pierwszy napotkany błąd, zwłaszcza przy uszkodzonym pliku z wielu
 * utworów naraz. */
function sprawdzUtwor(wpis: unknown, indeks: number, powody: string[]): void {
  if (typeof wpis !== 'object' || wpis === null) {
    powody.push(`Pozycja ${indeks}: nie jest obiektem.`);
    return;
  }
  const u = wpis as Record<string, unknown>;
  if (typeof u['id'] !== 'string' || u['id'] === '') powody.push(`Pozycja ${indeks}: brak poprawnego "id".`);
  if (typeof u['tytul'] !== 'string' || u['tytul'] === '') powody.push(`Pozycja ${indeks}: brak poprawnego "tytul".`);
  if (!(u['wykonawca'] === null || typeof u['wykonawca'] === 'string')) {
    powody.push(`Pozycja ${indeks}: "wykonawca" musi być tekstem albo null.`);
  }
  if (u['zrodlo'] !== 'wbudowany' && u['zrodlo'] !== 'reczny') {
    powody.push(`Pozycja ${indeks}: "zrodlo" musi być "wbudowany" albo "reczny".`);
  }
  if (typeof u['rawTab'] !== 'string' || u['rawTab'] === '') {
    powody.push(`Pozycja ${indeks}: brak "rawTab".`);
  }
  if (typeof u['dodanyO'] !== 'string') powody.push(`Pozycja ${indeks}: brak "dodanyO".`);
  if (!(u['zmienionyO'] === null || typeof u['zmienionyO'] === 'string')) {
    powody.push(`Pozycja ${indeks}: "zmienionyO" musi być tekstem albo null.`);
  }
  // `transpozycja`/`capo` (etap 7): OPCJONALNE, więc brak pola (rekord sprzed
  // tego etapu) jest przyjmowany bez zastrzeżeń. Gdy pole JEST obecne, musi
  // być liczbą całkowitą - tekst albo ułamek to powód odrzucenia, jak przy
  // każdym innym polu tej funkcji, nigdy ciche domyślenie się wartości.
  if (u['transpozycja'] !== undefined && !Number.isInteger(u['transpozycja'])) {
    powody.push(`Pozycja ${indeks}: "transpozycja" musi być liczbą całkowitą.`);
  }
  if (u['capo'] !== undefined && !Number.isInteger(u['capo'])) {
    powody.push(`Pozycja ${indeks}: "capo" musi być liczbą całkowitą.`);
  }
  // `tagi`/`ulubiony` (wyszukiwanie i filtry): ten sam wzorzec co
  // `transpozycja`/`capo` wyżej - OPCJONALNE, brak pola akceptowany bez
  // zastrzeżeń, obecne musi mieć właściwy kształt.
  if (u['tagi'] !== undefined && (!Array.isArray(u['tagi']) || !u['tagi'].every((t) => typeof t === 'string'))) {
    powody.push(`Pozycja ${indeks}: "tagi" musi być tablicą samych tekstów.`);
  }
  if (u['ulubiony'] !== undefined && typeof u['ulubiony'] !== 'boolean') {
    powody.push(`Pozycja ${indeks}: "ulubiony" musi być wartością logiczną.`);
  }
}

/** Sprawdza pojedynczą `Setlista` z paczki, ten sam wzorzec co `sprawdzUtwor`
 * wyżej (kompletna lista powodów, nie pierwszy napotkany błąd). Identyfikatory
 * w `utwory` NIE są tu sprawdzane pod kątem istnienia wśród `utwory` paczki:
 * setlista wskazująca na utwór skasowany w międzyczasie nie jest błędem
 * pliku, tylko stanem do rozstrzygnięcia w interfejsie (pominięcie przy
 * graniu, brakująca pozycja przy edycji - patrz `ui/setlista.ts`). */
function sprawdzSetliste(wpis: unknown, indeks: number, powody: string[]): void {
  if (typeof wpis !== 'object' || wpis === null) {
    powody.push(`Setlista ${indeks}: nie jest obiektem.`);
    return;
  }
  const s = wpis as Record<string, unknown>;
  if (typeof s['id'] !== 'string' || s['id'] === '') powody.push(`Setlista ${indeks}: brak poprawnego "id".`);
  if (typeof s['nazwa'] !== 'string') powody.push(`Setlista ${indeks}: brak poprawnej "nazwa".`);
  const utwory = s['utwory'];
  if (!Array.isArray(utwory) || !utwory.every((u) => typeof u === 'string')) {
    powody.push(`Setlista ${indeks}: "utwory" musi być tablicą samych identyfikatorów tekstowych.`);
  }
  if (typeof s['utworzonaO'] !== 'string') powody.push(`Setlista ${indeks}: brak "utworzonaO".`);
}

/** Waliduje dane wczytane z pliku PRZED jakimkolwiek zapisem do bazy. Czysta
 * funkcja, bez DOM-u i bez przeglądarki, żeby dało się ją sprawdzić samym
 * Vitestem, bez uruchamiania niczego graficznego. */
export function sprawdzPaczke(dane: unknown): { ok: true; paczka: PaczkaEksportu } | { ok: false; powody: string[] } {
  if (typeof dane !== 'object' || dane === null) {
    return { ok: false, powody: ['Plik nie zawiera obiektu JSON.'] };
  }
  const rekord = dane as Record<string, unknown>;
  const powody: string[] = [];

  if (rekord['format'] !== 'ognisko-spiewnik') {
    powody.push('Brak pola "format" albo to nie jest plik śpiewnika Ognisko.');
  }
  if (rekord['wersjaFormatu'] !== WERSJA_FORMATU_PACZKI) {
    powody.push(`Nieznana wersja formatu pliku: ${JSON.stringify(rekord['wersjaFormatu'])}.`);
  }
  if (typeof rekord['zapisanoO'] !== 'string') {
    powody.push('Brak daty zapisu paczki.');
  }

  const utwory = rekord['utwory'];
  if (!Array.isArray(utwory)) {
    powody.push('Pole "utwory" nie jest listą.');
  } else {
    utwory.forEach((wpis, i) => sprawdzUtwor(wpis, i, powody));
  }

  // `setlisty` jest OPCJONALNE (zgodność wstecz, patrz `PaczkaEksportu`) -
  // brak pola przechodzi bez zastrzeżeń, obecne musi być poprawną listą.
  const setlisty = rekord['setlisty'];
  if (setlisty !== undefined) {
    if (!Array.isArray(setlisty)) {
      powody.push('Pole "setlisty" nie jest listą.');
    } else {
      setlisty.forEach((wpis, i) => sprawdzSetliste(wpis, i, powody));
    }
  }

  if (powody.length > 0) return { ok: false, powody };
  return { ok: true, paczka: dane as PaczkaEksportu };
}

/** Buduje paczkę z aktualnej zawartości bazy i uruchamia pobranie pliku
 * przez ukryty `<a download>`, jedyny sposób zapisu pliku na dysk dostępny
 * bez backendu. */
export async function eksportujDoPliku(): Promise<void> {
  const [utwory, setlisty] = await Promise.all([listaUtworow(), listaSetlist()]);
  const paczka: PaczkaEksportu = {
    format: 'ognisko-spiewnik',
    wersjaFormatu: WERSJA_FORMATU_PACZKI,
    zapisanoO: new Date().toISOString(),
    utwory,
    setlisty,
  };

  const blob = new Blob([JSON.stringify(paczka, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const data = paczka.zapisanoO.slice(0, 10); // RRRR-MM-DD z tego samego znacznika czasu, nie osobne wywołanie zegara
  const kotwica = document.createElement('a');
  kotwica.href = url;
  kotwica.download = `spiewnik-${data}.json`;

  // Kotwica MUSI trafić do dokumentu przed kliknięciem. Safari nie uruchamia
  // pobrania z elementu spoza drzewa, więc wariant "utwórz i kliknij" kończy
  // się tam brakiem reakcji, bez żadnego błędu.
  kotwica.style.display = 'none';
  document.body.appendChild(kotwica);
  kotwica.click();
  kotwica.remove();

  // Zwolnienie adresu jest ODROCZONE, a nie w `finally`, i to nie jest
  // ostrożność: unieważnienie obiektu w tej samej pętli zdarzeń, w której padło
  // kliknięcie, potrafi przerwać dopiero rozpoczynające się pobranie. Plik
  // znika wtedy bez komunikatu, czyli w najgorszy możliwy sposób dla funkcji,
  // która ma być zabezpieczeniem śpiewnika.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Odtwarza bazę z pliku: WALIDACJA najpierw, zapis dopiero po niej, jedną
 * transakcją NA OBU magazynach (`utwory` i `setlisty`), która czyści je i
 * wpisuje komplet naraz. Przerwana transakcja (np. limit miejsca w połowie
 * wpisywania) cofa się w całości z automatu, bo `clear()` i wszystkie `put()`
 * na obu magazynach żyją w jednej transakcji IndexedDB - baza zostaje wtedy
 * dokładnie w stanie sprzed importu. */
export async function importujZPliku(plik: File): Promise<{ liczbaUtworow: number }> {
  const tekst = await plik.text();

  let dane: unknown;
  try {
    dane = JSON.parse(tekst);
  } catch {
    throw new BladMagazynu('Plik nie jest poprawnym plikiem JSON.');
  }

  const wynik = sprawdzPaczke(dane);
  if (!wynik.ok) {
    throw new BladMagazynu(`Plik odrzucony: ${wynik.powody.join(' ')}`);
  }

  const db = await otworzBaze();
  await new Promise<void>((resolve, reject) => {
    // OBA magazyny w JEDNEJ transakcji: połowiczny import, w którym setlisty
    // wskazywałyby na utwory sprzed importu (albo odwrotnie), byłby gorszy
    // niż odrzucenie pliku. `setlisty` w pliku jest opcjonalne, ale magazyn
    // setlist i tak wchodzi do tej transakcji - import ma znaczyć "zastąp
    // całość", więc plik sprzed etapu 8 (bez pola `setlisty`) zastępuje
    // istniejące setlisty pustym zbiorem, tak samo jak zastępuje `utwory`.
    const transakcja = db.transaction([MAGAZYN_UTWORY, MAGAZYN_SETLISTY], 'readwrite');
    const storeUtwory = transakcja.objectStore(MAGAZYN_UTWORY);
    const storeSetlisty = transakcja.objectStore(MAGAZYN_SETLISTY);
    storeUtwory.clear();
    for (const utwor of wynik.paczka.utwory) storeUtwory.put(utwor);
    storeSetlisty.clear();
    for (const setlista of wynik.paczka.setlisty ?? []) storeSetlisty.put(setlista);

    transakcja.oncomplete = () => resolve();
    transakcja.onerror = () => {
      const blad = transakcja.error;
      const komunikat = blad?.name === 'QuotaExceededError'
        ? 'Przekroczono limit miejsca na dane w przeglądarce, import przerwany.'
        : `Import się nie powiódł: ${blad?.message ?? 'nieznany błąd'}.`;
      reject(new BladMagazynu(komunikat));
    };
    transakcja.onabort = () => reject(
      new BladMagazynu('Import przerwany, baza zostaje w stanie sprzed importu.'),
    );
  });

  return { liczbaUtworow: wynik.paczka.utwory.length };
}

/** Dołącza zawartość pliku do istniejącego śpiewnika, BEZ kasowania niczego -
 * to jest cała różnica względem `importujZPliku` wyżej (etap 9, most z
 * Ultimate Guitar): człowiek chce DOŁOŻYĆ ulubione z serwisu, a nie zastąpić
 * nimi to, co ma. Ta sama walidacja PRZED zapisem co przy zastępowaniu.
 *
 * Utwór o identyfikatorze już obecnym w bazie jest POMIJANY, nigdy
 * nadpisywany: człowiek mógł ręcznie poprawić tab albo tytuł, a ponowny
 * import z serwisu cofnąłby tę pracę po cichu, gdyby nadpisywał. Ten sam
 * powód sprawia, że dwukrotne dołączenie tego samego pliku jest bezpieczne -
 * druga próba widzi identyfikatory z pierwszej i pomija je wszystkie.
 *
 * Setlisty z pliku dołączanego są tu pomijane CAŁKOWICIE (magazyn `setlisty`
 * nie wchodzi nawet do transakcji): plik z bookmarkletu ich nie ma, a
 * scalanie cudzych setlist z własnymi byłoby regułą bez oczywistego
 * znaczenia. */
export async function dolaczZPliku(plik: File): Promise<{ dodano: number; pominieto: number }> {
  const tekst = await plik.text();

  let dane: unknown;
  try {
    dane = JSON.parse(tekst);
  } catch {
    throw new BladMagazynu('Plik nie jest poprawnym plikiem JSON.');
  }

  const wynik = sprawdzPaczke(dane);
  if (!wynik.ok) {
    throw new BladMagazynu(`Plik odrzucony: ${wynik.powody.join(' ')}`);
  }

  const db = await otworzBaze();
  return new Promise<{ dodano: number; pominieto: number }>((resolve, reject) => {
    const transakcja = db.transaction(MAGAZYN_UTWORY, 'readwrite');
    const store = transakcja.objectStore(MAGAZYN_UTWORY);
    let dodano = 0;
    let pominieto = 0;

    // Odczyt klucza PRZED ewentualnym zapisem, w TEJ SAMEJ transakcji: każde
    // `getKey` trzyma transakcję otwartą, dopóki jej `onsuccess` (niżej) nie
    // zdąży ewentualnie dołożyć `put()` - transakcja IndexedDB żyje tak długo,
    // jak długo mają się w niej pojawić kolejne żądania, więc ta pętla nie
    // zamyka jej przedwcześnie mimo że wszystkie `getKey` startują od razu.
    for (const utwor of wynik.paczka.utwory) {
      const zadanieOdczytu = store.getKey(utwor.id);
      zadanieOdczytu.onsuccess = () => {
        if (zadanieOdczytu.result === undefined) {
          store.put(utwor);
          dodano += 1;
        } else {
          pominieto += 1;
        }
      };
    }

    transakcja.oncomplete = () => resolve({ dodano, pominieto });
    transakcja.onerror = () => {
      const blad = transakcja.error;
      const komunikat = blad?.name === 'QuotaExceededError'
        ? 'Przekroczono limit miejsca na dane w przeglądarce, dołączenie przerwane.'
        : `Dołączenie się nie powiodło: ${blad?.message ?? 'nieznany błąd'}.`;
      reject(new BladMagazynu(komunikat));
    };
    transakcja.onabort = () => reject(
      new BladMagazynu('Dołączenie przerwane, baza zostaje w stanie sprzed operacji.'),
    );
  });
}
